/**
 * Sales-order approval "facts" — the catalog of fields an Approval step's
 * `appliesWhen` condition (and the split-rule builder) can reference, and the
 * shape of the fact record the workflow evaluates conditions against.
 *
 * Facts are computed once by the Compute Order Facts step (stamped onto the
 * draft lines) and aggregated into a per-order record at evaluation time.
 * Operators are the generic rule-evaluator set (see `ruleEvaluator.ts`). Boolean
 * facts are exposed as COUNTS (e.g. `order.listPriceMismatchCount`) so a numeric
 * `gt 0` condition works cleanly with the free-form rule builder rather than
 * relying on boolean string coercion.
 */

export type ApprovalFactOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "in"
  | "not_in"
  | "is_null"
  | "is_not_null";

export interface ApprovalFactField {
  /** Dot-path key into the fact record (e.g. "order.total"). */
  key: string;
  label: string;
  dataType: "number" | "string" | "boolean" | "enum" | "string_array";
  operators: ApprovalFactOperator[];
  enumValues?: string[];
  description?: string;
}

const NUMBER_OPS: ApprovalFactOperator[] = [
  "gte",
  "gt",
  "lte",
  "lt",
  "eq",
  "neq",
];
const STRING_OPS: ApprovalFactOperator[] = ["eq", "neq", "in", "not_in"];
const ARRAY_OPS: ApprovalFactOperator[] = ["contains", "not_contains"];

/**
 * The catalog powering the approval/split condition builders. Keep keys in sync
 * with `buildSalesOrderFactRecord` on the backend.
 */
export const SALES_ORDER_APPROVAL_FIELDS: ApprovalFactField[] = [
  {
    key: "order.total",
    label: "Order total",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Sum of line amounts (qty × unit price when amount is absent).",
  },
  {
    key: "order.lineCount",
    label: "Line count",
    dataType: "number",
    operators: NUMBER_OPS,
  },
  {
    key: "order.currency",
    label: "Currency",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "order.listPriceMismatchCount",
    label: "# lines with an unapproved price below list",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Lines whose unit price is below list with no approved discount — use `gt 0` to gate on any mismatch.",
  },
  {
    key: "order.noListPriceCount",
    label: "# lines with no list price",
    dataType: "number",
    operators: NUMBER_OPS,
    description: "Lines whose product has no list price in the ERP.",
  },
  {
    key: "order.insufficientInventoryCount",
    label: "# lines with insufficient inventory",
    dataType: "number",
    operators: NUMBER_OPS,
  },
  {
    key: "order.productGroups",
    label: "Product groups present",
    dataType: "string_array",
    operators: ARRAY_OPS,
    description: "Use `contains <group>` to gate on a product group.",
  },
  {
    key: "customer.tier",
    label: "Customer tier",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "customer.type",
    label: "Customer type",
    dataType: "string",
    operators: STRING_OPS,
  },
];

/**
 * Per-LINE fields a split rule can reference. A split rule that matches a line
 * routes it into a separate draft (lines matching no rule stay on the original).
 * Reproduces the legacy ship-from-location strategy declaratively, e.g.
 * `variant.activeInZhuhai eq ORDERABLE` → a "China" split.
 */
export const SALES_ORDER_SPLIT_LINE_FIELDS: ApprovalFactField[] = [
  {
    key: "line.origin",
    label: "Line origin",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.warehouse",
    label: "Line warehouse",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.sku",
    label: "Line SKU",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.productGroup",
    label: "Line product group",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.inventoryStatus",
    label: "Line inventory status",
    dataType: "enum",
    operators: STRING_OPS,
    enumValues: ["SUFFICIENT", "INSUFFICIENT", "UNKNOWN", "INACTIVE"],
  },
  {
    key: "variant.activeInZhuhai",
    label: "Variant active in Zhuhai",
    dataType: "string",
    operators: STRING_OPS,
    description: 'e.g. "ORDERABLE" for China-orderable products.',
  },
  {
    key: "variant.prodview",
    label: "Variant product view",
    dataType: "string",
    operators: STRING_OPS,
  },
];
